import { cloneElement, CSSProperties, FC, ReactElement, ReactNode } from 'react';
import { Button, Dropdown, Space } from 'antd';
import { TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';

export type FilterButtonGroupSegment = {
    dropdown?: ReactElement;
    onDropdownOpenChange?: (open: boolean) => void;
    onClick?: () => void;
    icon?: ReactNode;
    key: string;
    label: ReactNode;
    maxWidth?: CSSProperties['maxWidth'];
    open?: boolean;
    placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
    renderTrigger?: (trigger: ReactElement) => ReactElement;
    style?: CSSProperties;
};

interface FilterButtonGroupProps {
    onRemove?: () => void;
    removeTooltip?: ReactNode;
    segments: FilterButtonGroupSegment[];
    size?: 'large' | 'middle' | 'small';
    style?: CSSProperties;
}

const defaultButtonStyle: CSSProperties = {
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

const FilterButtonGroup: FC<FilterButtonGroupProps> = ({
    onRemove,
    removeTooltip = 'Remove',
    segments,
    size = 'small',
    style,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Space.Compact size={size} style={{ borderRadius: token.borderRadius, marginRight: '0.5rem', ...style }}>
            {segments.map((segment) => {
                const interactive = Boolean(segment.dropdown || segment.onClick || segment.renderTrigger);
                const button = (
                    <DropdownButton
                        key={segment.key}
                        directionIconProps={{ style: { display: segment.dropdown ? undefined : 'none' } }}
                        onClick={segment.onClick}
                        size={size}
                        style={{
                            ...defaultButtonStyle,
                            maxWidth: segment.maxWidth ?? 16 * 12,
                            pointerEvents: interactive ? undefined : 'none',
                            ...segment.style,
                        }}
                    >
                        {segment.icon}
                        <small
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                lineHeight: '1rem',
                                minWidth: 0,
                            }}
                        >
                            {segment.label}
                        </small>
                    </DropdownButton>
                );

                if (!segment.dropdown) {
                    return segment.renderTrigger
                        ? cloneElement(segment.renderTrigger(button), { key: segment.key })
                        : button;
                }

                return (
                    <Dropdown
                        key={segment.key}
                        trigger={['click']}
                        placement={segment.placement ?? 'bottomLeft'}
                        popupRender={() => segment.dropdown}
                        open={segment.open}
                        onOpenChange={segment.onDropdownOpenChange}
                    >
                        {button}
                    </Dropdown>
                );
            })}
            {onRemove && (
                <SmallTooltip title={removeTooltip} placement='bottom'>
                    <Button
                        size={size}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onRemove();
                        }}
                    >
                        <TbX style={{ color: token.colorTextTertiary, display: 'block' }} />
                    </Button>
                </SmallTooltip>
            )}
        </Space.Compact>
    );
};

export default FilterButtonGroup;
